'use client';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import formChooseServiceSchema from '@/schemas/formChooseService';
import { Service } from '@/services/api/services-api';
import { currencyFormat } from '@/utils/currency';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FormChooseServiceProps } from './FormChooseService.types';

type TForm = z.input<ReturnType<typeof formChooseServiceSchema>>;

const FormChooseService: React.FC<FormChooseServiceProps> = ({
  services,
  organization
}) => {
  // i18n text
  const { t } = useTranslation('newBooking');
  const Mixpanel = useMixPanel();

  //store
  const addStep = useRootStore((store) => store.addStep);
  const currentStep = useRootStore((store) => store.currentStep);
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );
  const stepServicesData = useRootStore((store) => store.stepServicesData);

  const SelectedServiceRef = useRef<null | Service>(null);
  const minParticipants = SelectedServiceRef.current?.minConsumers || 1;
  const maxParticipants = SelectedServiceRef.current?.maxConsumers || 4;

  // Initializing useForm hook with the schema and default values
  const form = useForm<TForm>({
    resolver: zodResolver(
      formChooseServiceSchema({ minParticipants, maxParticipants })
    ),
    defaultValues: {
      service: stepServicesData?.service,
      participants: stepServicesData?.participants
    }
  });

  // get price and participant from form with listeners
  const service = useWatch({ control: form.control, name: 'service' });
  const price = useWatch({ control: form.control, name: 'service' })?.price;
  const description = services?.find(
    (serv) => serv.id === service?.id
  )?.description;

  const participants = useWatch({
    control: form.control,
    name: 'participants'
  });

  const selectedService = form.getValues().service;
  const total = price * Number(participants);
  const recervationPct = form.getValues()?.service?.reservationPct;
  const deposit = total * (recervationPct / 100);

  async function onSubmit(data: TForm) {
    const service = data.service;
    const participants = data.participants;
    loadStepServicesData({
      service: {
        ...service,
        rangeDivisions: services.find((serv) => serv.id === service.id)
          ?.rangeDivisions
      },
      participants
    });

    addStep(currentStep);
  }

  return (
    <Form {...form}>
      <h2 className="font-bold mb-6">{t('stepServiceTitle')}</h2>
      <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-8`}>
        <FormField
          control={form.control}
          name="service"
          render={({ field }) => {
            return (
              <FormItem>
                <Select
                  onValueChange={(e) => {
                    Mixpanel.track('press select activity', {
                      screen: 'New Booking',
                      Activity: services.find((act: any) => act.id === e)?.name
                    });
                    const selectedServices = services.find(
                      (act: any) => act.id === e
                    );
                    field.onChange(selectedServices);
                    form.setValue(
                      'participants',
                      selectedServices?.minConsumers || 1
                    );
                  }}
                >
                  <FormControl>
                    <SelectTrigger
                      className="w-full justify-between"
                      custom={
                        field.value ? (
                          <div>
                            <p>
                              {currencyFormat(
                                field.value?.price,
                                organization.currency
                              )}
                            </p>
                          </div>
                        ) : undefined
                      }
                    >
                      <div>
                        {field.value ? (
                          <p>{field.value.name}</p>
                        ) : (
                          t('activity')
                        )}
                      </div>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {services?.map((service: any, i: number) => (
                      <SelectItem value={service.id} key={`${service.id}${i}`}>
                        <div className="flex justify-between">
                          <p>{service.name}</p>
                          <p>
                            {currencyFormat(
                              service.price,
                              organization.currency
                            )}
                          </p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        {description ? (
          <div className="bg-accent rounded p-3 text-sm">
            <p>{description}</p>
          </div>
        ) : null}
        <FormField
          control={form.control}
          name="participants"
          render={({ field }) => {
            return (
              <FormItem>
                <Select
                  onValueChange={(value) => {
                    const parsedValue = parseInt(value);
                    if (typeof parsedValue === 'number') {
                      field.onChange(parsedValue);
                    }
                    Mixpanel.track('press select paticipants', {
                      screen: 'New Booking',
                      participantsAmount: value
                    });
                  }}
                  defaultValue={undefined}
                  value={field.value ? String(field.value) : undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('participant')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {selectedService
                      ? [...Array(selectedService?.maxConsumers)].map(
                          (_, i) => {
                            const participant = i + 1;
                            return participant === 1 ? (
                              <SelectItem
                                value={String(participant)}
                                key={participant}
                              >
                                {t(
                                  'stepParticipants.participants.singularParticipant',
                                  { participant: participant }
                                )}
                              </SelectItem>
                            ) : (
                              <SelectItem
                                value={String(participant)}
                                key={participant}
                              >
                                {t(
                                  'stepParticipants.participants.pluralParticipant',
                                  { participant: participant }
                                )}
                              </SelectItem>
                            );
                          }
                        )
                      : undefined}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        {total ? (
          <div className="w-full bg-accent rounded p-3 space-y-2">
            <div className="flex justify-between">
              <p>{t('stepParticipants.total')}</p>
              <p>
                {currencyFormat(
                  form.control._formValues?.service?.price *
                    Number(form.control._formValues?.participants),
                  organization.currency
                )}
              </p>
            </div>
            <div className="flex justify-between">
              <p>{t('stepParticipants.deposit')}</p>
              <p>{currencyFormat(deposit, organization.currency)}</p>
            </div>
          </div>
        ) : null}
        <Button
          onClick={() => {
            Mixpanel.track('press continue button', {
              screen: 'New Booking'
            });
          }}
          type="submit"
          className="w-full"
        >
          {t('stepParticipants.form.continue')}
        </Button>
      </form>
    </Form>
  );
};

export default FormChooseService;
